import { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    onlyFills,
    toggleTradeType,
    TRADE_TYPES,
    tradeTypeLabel,
    type TradeTypeId,
    type WalletTrade,
} from './tradeTypes';

export const TRADES_PAGE_SIZE = 10;
const GRID = '150px 50px 70px 90px 90px';
const GRID_WITH_TYPE = '150px 90px 50px 70px 90px 90px';

interface WalletTradesProps {
    trades: readonly WalletTrade[] | undefined;
    loading: boolean;
    failed: boolean;
    onRetry: () => void;
    types: readonly TradeTypeId[];
    onTypesChange: (types: TradeTypeId[]) => void;
}

function Cell({
    children,
    color,
    head = false,
}: {
    children: React.ReactNode;
    color?: string;
    head?: boolean;
}) {
    return (
        <Typography
            sx={{
                fontSize: head ? '10px' : '11px',
                fontFamily: head ? undefined : 'monospace',
                fontWeight: head ? 700 : 400,
                textTransform: head ? 'uppercase' : undefined,
                letterSpacing: head ? '0.06em' : undefined,
                color: color ?? (head ? 'text.disabled' : 'text.primary'),
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </Typography>
    );
}

function compact(n: number): string {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return n.toFixed(0);
}

export function splitBySide(trades: readonly WalletTrade[]): {
    yes: WalletTrade[];
    no: WalletTrade[];
} {
    const newestFirst = [...trades].sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );
    return {
        yes: newestFirst.filter(t => t.outcomeIndex !== 1),
        no: newestFirst.filter(t => t.outcomeIndex === 1),
    };
}

function SideTable({
    side,
    trades,
    showType,
}: {
    side: 'YES' | 'NO';
    trades: readonly WalletTrade[];
    showType: boolean;
}) {
    const theme = useTheme();
    const [count, setCount] = useState(TRADES_PAGE_SIZE);
    const grid = showType ? GRID_WITH_TYPE : GRID;

    return (
        <Box data-testid={`wallet-trades-${side.toLowerCase()}`}>
            <Typography
                sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    mb: 0.25,
                    color:
                        side === 'YES'
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                }}
            >
                {side} {showType ? 'trades' : 'fills'} ({trades.length})
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: grid,
                    columnGap: 1.5,
                    py: 0.25,
                }}
            >
                <Cell head>Time</Cell>
                {showType && <Cell head>Type</Cell>}
                <Cell head>Side</Cell>
                <Cell head>Price</Cell>
                <Cell head>Shares</Cell>
                <Cell head>Value</Cell>
            </Box>
            {trades.slice(0, count).map(t => (
                <Box
                    key={t.id}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: grid,
                        columnGap: 1.5,
                        py: 0.25,
                    }}
                >
                    <Cell color={theme.palette.text.secondary}>
                        {t.timestamp.toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </Cell>
                    {showType && <Cell>{tradeTypeLabel(t.tradeType)}</Cell>}
                    <Cell
                        color={
                            t.side === 'buy'
                                ? theme.palette.success.main
                                : theme.palette.error.main
                        }
                    >
                        {t.side === 'buy' ? 'Buy' : 'Sell'}
                    </Cell>
                    <Cell>{(t.price * 100).toFixed(1)}¢</Cell>
                    <Cell>{compact(t.amount)}</Cell>
                    <Cell>${compact(t.quoteAmount)}</Cell>
                </Box>
            ))}
            {trades.length > count && (
                <Button
                    size="small"
                    onClick={() => setCount(c => c + TRADES_PAGE_SIZE)}
                    sx={{
                        fontSize: '11px',
                        textTransform: 'none',
                        mt: 0.25,
                        ml: -0.5,
                    }}
                >
                    Show more ({trades.length - count} left)
                </Button>
            )}
        </Box>
    );
}

function TypeFilter({
    types,
    onTypesChange,
}: {
    types: readonly TradeTypeId[];
    onTypesChange: (types: TradeTypeId[]) => void;
}) {
    return (
        <Box
            data-testid="trade-type-filter"
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                pl: 3,
                pt: 0.75,
            }}
        >
            {TRADE_TYPES.map(option => {
                const on = types.includes(option.id);
                return (
                    <Chip
                        key={option.id}
                        label={option.label}
                        size="small"
                        onClick={() =>
                            onTypesChange(toggleTradeType(types, option.id))
                        }
                        color={on ? 'primary' : 'default'}
                        variant={on ? 'filled' : 'outlined'}
                        sx={{ height: 20, fontSize: '10px', fontWeight: 600 }}
                    />
                );
            })}
        </Box>
    );
}

function TradesBody({
    trades,
    loading,
    failed,
    onRetry,
    types,
}: Omit<WalletTradesProps, 'onTypesChange'>) {
    if (failed) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    pl: 3,
                }}
            >
                <Typography sx={{ fontSize: '12px', color: 'error.main' }}>
                    The wallet data could not be loaded.
                </Typography>
                <Button
                    size="small"
                    onClick={onRetry}
                    sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
                >
                    Try again
                </Button>
            </Box>
        );
    }
    if (loading || !trades) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1,
                    pl: 3,
                }}
            >
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    Loading the trades of this wallet
                </Typography>
            </Box>
        );
    }
    if (trades.length === 0) {
        return (
            <Typography
                sx={{ fontSize: '12px', color: 'text.disabled', py: 1, pl: 3 }}
            >
                No trades in this outcome
            </Typography>
        );
    }

    const { yes, no } = splitBySide(trades);
    const showType = !onlyFills(types);

    return (
        <Box
            data-testid="wallet-trades"
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                columnGap: 6,
                rowGap: 1,
                pl: 3,
                py: 0.75,
            }}
        >
            {yes.length > 0 && (
                <SideTable side="YES" trades={yes} showType={showType} />
            )}
            {no.length > 0 && (
                <SideTable side="NO" trades={no} showType={showType} />
            )}
        </Box>
    );
}

export function WalletTrades(props: WalletTradesProps) {
    return (
        <Box>
            <TypeFilter
                types={props.types}
                onTypesChange={props.onTypesChange}
            />
            <TradesBody
                trades={props.trades}
                loading={props.loading}
                failed={props.failed}
                onRetry={props.onRetry}
                types={props.types}
            />
        </Box>
    );
}
